import { Box } from "@mui/material";
import { useMediaQuery } from "@react-hook/media-query";
import moment from "moment";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import Header from "../components/Header";
import MobileFooter from "../components/MobileFooter";
import MobileHeader from "../components/MobileHeader";
import Notification from "../components/Notification";
import SubscriptionBanner from "../components/SubscriptionBanner";
import TutorialModeLayer from "../components/Tutorial/Layer";
import LoginModal from "../components/LoginModal";
import ApprovalPendingModal from "../components/ApprovalPendingModal";
import AnalyticsTracker from "../components/AnalyticsTracker";
import useCacheBuster from "../hooks/useCacheBuster";
import { RootState } from "../store";
import { set } from "../store/globalSlice";
import { checkEmptyObj, checkIf, defaultFormat, setThemeBarColors } from "../utils";
import { defaultThemeBarColor } from "../theme";
import { GetInTouch } from "../components/GetInTouch";
import { useTranslation } from "react-i18next";
import { LoadingTransitionProvider } from "../contexts/LoadingTransitionContext";

export const Layout = () => {
  useCacheBuster();
  const mobile = useMediaQuery("only screen and (max-width: 600px)");
  const tutorialMode = useSelector((state: RootState) => state.global.tutorialMode);
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  // Set document direction based on language
  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  // Re-push the tenant status/bottom bar colors once we're inside the app.
  // The boot-time call in theme/index.tsx can run before the native WebView
  // bridge (window.App) is ready and then silently no-op, leaving already
  // logged-in users on the old colors. Re-applying on mount — and whenever the
  // app is foregrounded — guarantees the bridge gets the current colors without
  // requiring a logout/login or reload.
  useEffect(() => {
    setThemeBarColors(defaultThemeBarColor);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        setThemeBarColors(defaultThemeBarColor);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(() => {
    if (!checkEmptyObj(user) && checkIf(user.activeSubscription)) {
      const lastTutorialShown = localStorage.getItem("lastTutorialDate");
      if (!lastTutorialShown) {
        // check if the user is on payment status page
        if (pathname !== "/payment-status") {
          navigate("/meal-plan");
          dispatch(set({ tutorialMode: true }));
          localStorage.setItem("lastTutorialDate", moment().locale("en").format(defaultFormat));
        }
      } else {
        const reShow = moment(lastTutorialShown).add(90, "days").isSameOrBefore(moment());
        if (reShow) {
          navigate("/meal-plan");
          dispatch(set({ tutorialMode: true }));
          localStorage.setItem("lastTutorialDate", moment().locale("en").format(defaultFormat));
        }
      }
    }
    // Remove setLoaded(true) since it's now handled by ComingSoonGuard
  }, [user]);

  return (
    <LoadingTransitionProvider>
      <div className={`${mobile ? "app mobile" : "app"}`}>
        <AnalyticsTracker />
        {mobile ? <MobileHeader /> : <Header />}
        <Box className={`${mobile ? "main mobile" : "main"}`}>
          <Outlet />
          <Notification />
          {!checkEmptyObj(user) ? <SubscriptionBanner /> : null}
          <GetInTouch />
        </Box>
        {mobile ? <MobileFooter /> : <Footer />}
        {tutorialMode && !checkEmptyObj(user) && checkIf(user.activeSubscription) ? <TutorialModeLayer /> : null}
        <LoginModal />
        <ApprovalPendingModal />
      </div>
    </LoadingTransitionProvider>
  );
};
